import { useMemo } from 'react';
import { FormSelect, FormTextField } from '@skupreme/shared/ui/form';
import { IntegrationOptions, IntegrationType } from '../../lib/integration';
import type { StepProps } from './types';

export type WmsWarehouse = {
  _id: string;
  name: string;
  integrationMetadata?: {
    displayName?: string;
    description?: string;
    logo?: { url?: string };
    customFields?: Record<string, WmsCustomField>;
  };
};

export type WmsCustomField = {
  type: 'text' | 'number' | 'boolean' | 'radio';
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: { label: string; value: string }[];
  validation?: { min?: number; max?: number; message?: string };
};

type ProviderTypeStepProps = StepProps & {
  showName: boolean;
  /** Changing an existing provider's type offers neither Manual nor its own. */
  changeTypeOnly: boolean;
  currentIntegrationType?: string;
  wmsWarehouses: WmsWarehouse[];
};

export function ProviderTypeStep({
  control,
  fieldError,
  isVisible,
  showName,
  changeTypeOnly,
  currentIntegrationType,
  wmsWarehouses,
}: ProviderTypeStepProps) {
  const options = useMemo(() => {
    const all = [
      ...IntegrationOptions.map(option => ({
        value: option.value,
        label: option.text,
      })),
      ...wmsWarehouses.map(warehouse => ({
        value: warehouse._id,
        label: warehouse.name,
      })),
    ];

    return changeTypeOnly
      ? all.filter(
          option =>
            ![IntegrationType.manual, currentIntegrationType].includes(
              option.value,
            ),
        )
      : all;
  }, [changeTypeOnly, currentIntegrationType, wmsWarehouses]);

  return (
    <div className={isVisible ? 'sk:flex sk:flex-col sk:gap-4' : 'sk:hidden'}>
      <FormSelect
        control={control}
        name="integration_type"
        label="Integration Type"
        placeholder="Select an integration"
        options={options}
        error={fieldError('integration_type')}
        isRequired
      />
      {showName ? (
        <FormTextField
          control={control}
          name="name"
          label="Storage Provider Name"
          error={fieldError('name')}
          isRequired
        />
      ) : null}
    </div>
  );
}
